import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ActionItem } from './lib/types';
import { Fact, facts } from './lib/decideRank';
import { FONT, RADIUS } from './lib/brand';

const NOT_STATED = 'Not stated';

/**
 * Impact · Effort · Risk · Confidence for one item. Full: four small tiles (queue
 * cards, the recommendation drawer). Compact: one line (Action Center rows). A
 * value Grok Bot has not sent reads "Not stated"; the basis is in the tooltip.
 */
export function ImpactTiles({ action, compact = false }: { action: ActionItem; compact?: boolean }) {
  const list = facts(action);
  if (compact) {
    return (
      <Box data-testid="impact-line" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.2, rowGap: 0.2, fontSize: 11.5, color: 'text.secondary' }}>
        {list.filter((f) => f.label === 'Impact' || f.label === 'Effort').map((f) => (
          <Box key={f.label} component="span">
            <Box component="span" sx={{ fontWeight: 700 }}>{f.label}</Box> {f.value || NOT_STATED}
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box data-testid="impact-tiles" role="list" aria-label="Impact, effort, risk and confidence" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(4, minmax(0,1fr))' }, gap: 0.8 }}>
      {list.map((f) => <Tile key={f.label} fact={f} />)}
    </Box>
  );
}

function Tile({ fact }: { fact: Fact }) {
  const body = (
    <Box role="listitem" tabIndex={fact.basis ? 0 : undefined} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.md, px: 1, py: 0.6, minWidth: 0, bgcolor: 'background.paper' }}>
      <Typography component="div" sx={{ fontFamily: FONT.heading, fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'text.secondary' }}>{fact.label}</Typography>
      <Typography component="div" sx={{ fontSize: 12.5, fontWeight: fact.value ? 700 : 400, color: fact.value ? 'text.primary' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {fact.value || NOT_STATED}
      </Typography>
    </Box>
  );
  return fact.basis ? <Tooltip title={fact.basis} describeChild>{body}</Tooltip> : body;
}
